import Head from 'next/head';
import { GetServerSideProps } from 'next';
import { useTranslation } from 'next-i18next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import {
  ApiJobListCards,
  ApiBreadcrumbs,
  buildSearchParams,
  fetcher,
  ApiBreadcrumbsOk,
  isJobListCardsOk,
  isBreadcrumbsOk,
  ObjectFilterParams,
} from '@utils/data-fetching';
import {
  useEffect, ReactNode, useMemo, useState,
} from 'react';
import { Button } from '@components/common';
import { BellIcon } from '@heroicons/react/24/solid';
import { useAppSelector } from '@store/index';
import {
  paginatedServerSideQuery,
  PaginatedServerSideQueryResponse,
} from '@utils/infinite-scrolling/processedServerSideQueries';
import { JobSeoWidget } from '@components/templates/job/SeoWidget';
import { JobAlertCard } from '@components/templates/job/list/JobAlertCard';
import { JobSearchFilters } from '@components/features/filters/JobSearchFilters';
import { useFiltersQueryParams } from '@hooks/filters/useFiltersQueryParams';
import { clsx } from 'clsx';
import { useCreateJobSearchAlert } from '@hooks/features/useCreateJobSearchAlert';
import { JobListType } from '@type/job-params';
import { getJobBulkApplyFeed } from '@store/reducers/jobSelector';
import NiceModal from '@ebay/nice-modal-react';
import { BulkApplyModal } from '@components/templates/job/apply/BulkApplyModal';
import { Breadcrumbs } from '@components/templates/job/Breadcrumbs';
import { gsspCheckCookieAuth, gsspGetLocale } from '@utils/gssp';
import { JobCardList } from '@components/templates/job/Card/JobCardList';
import { GuaranteedHiringBanner } from '@components/templates/employer/offer/GuaranteedHiringBanner';
import { getJobListMetaDescription, getJobListParametersFromSlug, getJobListTitle } from '@utils/job';
import { getCanonicalUrl } from '@services/i18nPaths';
import { useRouter } from 'next/router';
import { JobSearchQueryParams, FiltersQueryParamsStates } from '@type/filters';
import { LayoutDefault } from '@components/layout/Layouts/LayoutDefault';
import { getJobListHeadTitle } from '@utils/job/buildJobListHeadTitle';
import { useSwrJobListBreadcrumbs, useSwrJobListCardsInfinite } from '@hooks/useSwrApi/endpoints/job';
import { FiltersObjectInterface } from '@type/form-types';
import { JOB_LIST_ITEMS_PER_PAGE } from 'src/constants';
import { getServerSideTranslation } from '@app/i18n';
import ogImage from '@images/social-media/bestie.png';
import { useSwrJobSearchSeo } from '@root/src/hooks/useSwrApi/endpoints/job/useSwrJobSearchSeo';
import { FilterDrawerProvider } from '@components/contexts/FilterDrawerContext';
import useWindowDimensions from '@hooks/useMediaQuery';
import { useHeaderInfoData } from '@components/layout/Header/MainNavbar/data/useHeaderInfoData';
import { useAuthClient } from '@hooks/common/useAuthClient/useAuthClient';

// Banner positioning constants
// Job Alert banner positions (how many cards to be displayed before the banner)
const JOB_ALERT_BANNER_POSITIONS = [12, 24] as const;

// Guaranteed Hiring banner positioning rules (how many cards to be displayed before the banner)
const HIRE_GUARANTEE_TABLET_START_POSITION = 2; // End of row 1 (visible without scrolling)


// Interface
// ******************************************
interface JobListPageProps {
  locale: Locale,
  infiniteQueryParams: PaginatedServerSideQueryResponse,
  jobListCardsFromServer: ApiSchema<'JobResult'> | null,
  breadcrumbsFromServer: ApiBreadcrumbsOk | null,
  seoDataFromServer: ApiSchema<'SeoData'> | null,
  isAuthenticated: boolean,
}

// Variable that we can mutate in order to pass the query params to the search input component (keyword) via HeaderSearchContext
// ******************************************
let headerSearchContextValue: FiltersQueryParamsStates<JobSearchQueryParams>;

// Api keys that need to be an array. The thing is, form multiples (checkbox, select) return a string when only one value
// is selected, and array when multiple values are selected. The API expects arrays for these keys, even if only one value is selected.
// ******************************************
const apiKeysAsArray = ['domain', 'careerLevels', 'employmentTypes', 'spokenLanguages', 'benefits'];

/**
 * Parse x-client-citylatlong header to extract latitude and longitude
 * @param headerValue - The header value in format "lat,lon"
 * @returns Object with _lat and _lon or null if invalid
 */
const parseLocationHeader = (headerValue: string | undefined): { _lat: string; _lon: string } | null => {
  if (!headerValue) return null;
  const parts = headerValue.split(',');
  if (parts.length !== 2) return null;
  const [lat, lon] = parts.map((part) => part.trim());
  // Basic validation - check if they're numeric
  if (!lat || !lon || Number.isNaN(Number(lat)) || Number.isNaN(Number(lon))) return null;
  // Basic range validation for latitude and longitude
  const latNum = Number(lat);
  const lonNum = Number(lon);
  if (latNum < -90 || latNum > 90 || lonNum < -180 || lonNum > 180) return null;
  return { _lat: lat, _lon: lon };
};

/**
 * @description Job List page
 * - **locale**: string - the app locale.
 * - **infiniteQueryParams**: PaginatedServerSideQueryResponse - the object that contains the query params for the infinite scrolling.
 * - **jobListCardsFromServer**: ApiSchema<'JobResult'> | null - the job list data fetched from the server.
 * - **breadcrumbs**: ApiBreadcrumbsOk | null - the breadcrumb object fetched from the server.
 * - **isAuthenticated**: boolean - a boolean that indicates whether the user is authenticated.
 */
const JobListPage = (props: JobListPageProps) => {
  // Destructure props
  const {
    locale,
    infiniteQueryParams,
    jobListCardsFromServer,
    breadcrumbsFromServer,
    seoDataFromServer,
    isAuthenticated,
  } = props;

  // Destructure paginatedQuery
  const {
    cursor,
    limit,
    objFilterParams,
  } = infiniteQueryParams;

  // Translation
  const { t } = useTranslation('common');
  const { isTablet } = useWindowDimensions();
  const { headerInfoData } = useHeaderInfoData(locale);
  const { isAuth } = useAuthClient();

  // Handle query params in browser URL, history and filtering
  // ***********************************************
  const {
    queryParams,
    setQueryParams,
    setCurrentPage,
  } = useFiltersQueryParams({
    serverQueryParams: objFilterParams,
    initialPage: cursor,
    source: 'jobList',
    t,
    paginationType: 'cursor',
  });

  // Next router
  const { asPath } = useRouter();

  // Fetch data
  const {
    data,
    size,
    setSize,
    isLoading,
    isEmpty,
    sizeAsRef,
    isValidating,
    showLoadMore,
  } = useSwrJobListCardsInfinite({
    locale,
    limit,
    serverSideData: jobListCardsFromServer ?? undefined,
    initialCursor: cursor,
    // Pass the query params to the hook
    urlParams: buildSearchParams(queryParams, '&', false, apiKeysAsArray),
  });
  const apiRequestId = data?.at(-1)?.apiRequestId;

  const { data: seoData } = useSwrJobSearchSeo({
    locale,
    serverSideData: seoDataFromServer,
    urlParams: buildSearchParams(queryParams, '?', false, apiKeysAsArray),
  });

  // SWR: Fetch breadcrumbs
  // ***********************************************
  const { data: breadcrumbs } = useSwrJobListBreadcrumbs({
    locale,
    shouldFetch: !isAuthenticated,
    urlParams: buildSearchParams(queryParams, '?', false, apiKeysAsArray),
    serverSideData: breadcrumbsFromServer ?? undefined,
  });

  // Data for Header Search Context
  // ***********************************************
  headerSearchContextValue = useMemo(() => ({
    queryParams,
    setQueryParams,
  }), [queryParams, setQueryParams]);

  // Reset context value when leaving the page - because the headerSearchContextValue is outside the component
  // It's not cleaned when doing client navigation - requires a hard refresh for cleanup
  useEffect(() => () => {
    headerSearchContextValue = {
      queryParams: {},
      setQueryParams: () => {},
    };
  }, []);

  // Helpers
  // ***********************************************
  const searchId = data?.at(0)?.searchId;
  const searchAlertExists = data?.at(0)?.jobSearchAlertExists;

  const showLoadingOverlay = useMemo(
    () => isValidating && !isLoading,
    [isValidating, isLoading],
  );

  // Filter labels state
  // ***********************************************
  const [filterLabels, setFilterLabels] = useState<FiltersObjectInterface | undefined>(undefined);

  // Metadata
  // *****************************************************
  const title = getJobListTitle(t, filterLabels);
  const headTitle = getJobListHeadTitle(t, locale, title);
  const metaDescription = getJobListMetaDescription(t, filterLabels);

  const ogUrl = getCanonicalUrl(asPath);
  const facebookAppId = process.env.NEXT_PUBLIC_FACEBOOK_APP_ID ?? '';
  const ogImageUrl = ogImage.src.startsWith('http') ? ogImage.src : `${process.env.NEXT_PUBLIC_BASE_URL}${ogImage.src}`;

  // Create job search alert hook
  // *****************************************************
  const {
    createJobSearchAlert,
    isCreatingJobSearchAlert,
    isSavedJobSearchAlert,
  } = useCreateJobSearchAlert({
    searchId: searchId || '',
    alertExists: !!searchAlertExists,
  });

  // Bulk Apply Feed (from REDUX)
  // *****************************************
  const bulkApplyFeed = useAppSelector(getJobBulkApplyFeed);

  useEffect(() => {
    if (bulkApplyFeed?.length) {
      void NiceModal.show(BulkApplyModal, { jobList: bulkApplyFeed, locale });
    }
  }, [bulkApplyFeed, locale]);

  // Render page component
  // *****************************************************
  return (
    <>
      {/* HEAD - title, meta, etc */}
      <Head>
        <title>{headTitle}</title>
        <meta name="description" content={metaDescription} />

        {/* Open Graph */}
        <meta property="og:url" content={ogUrl} />
        <meta property="og:type" content="website" />
        <meta property="og:title" content={headTitle} />
        <meta property="og:description" content={metaDescription} />
        <meta property="fb:app_id" content={facebookAppId} />
        <meta property="og:image" content={ogImageUrl} />
      </Head>

      {/* PAGE */}
      <div className="min-h-screen bg-surface-100 md:min-h-3/4-screen">

        {/* Filters */}
        <JobSearchFilters queryParams={queryParams} setQueryParams={setQueryParams} setFilterLabels={setFilterLabels} />

        <div className="container relative mt-5">

          {/* Breadcrumbs */}
          {breadcrumbs && <Breadcrumbs className="mb-1" breadcrumbs={breadcrumbs} />}

          {/* Total items + Save job search */}
          <div className="flex flex-col items-start justify-between gap-4 mb-5 sm:flex-row sm:gap-0">
            {searchId && (
              <Button
                rounding="full"
                size="xs"
                className={clsx('shrink-0 !bg-surface-100 !text-sm !text-pink hover:!bg-pink/10 hover:!shadow-none', isSavedJobSearchAlert && 'pointer-events-none')}
                isLoading={isCreatingJobSearchAlert}
                spinnerClass="w-4 h-4 -ml-1 mr-2"
                onClick={createJobSearchAlert}
              >
                {!isCreatingJobSearchAlert && <BellIcon className="mr-2 -ml-1 size-4" />}
                { isSavedJobSearchAlert ? t('saved.job.alert') : t('save.job.alert') }
              </Button>
            )}
          </div>

          {/* Desktop: Guaranteed Hiring banner as first row - only show if user doesn't have package */}
          {!isTablet && (!isAuth || (headerInfoData && !headerInfoData.hireGuaranteeActive)) && (
            <div className="grid grid-cols-1 mb-4 md:grid-cols-2 lg:grid-cols-3">
              <div className="col-span-1 md:col-span-2 lg:col-span-3">
                <GuaranteedHiringBanner />
              </div>
            </div>
          )}

          {/* Infinite list */}
          <JobCardList
            data={data}
            trackingData={{
              listType: 'search',
              listUuid: apiRequestId,
            }}
            t={t}
            setCurrentPage={setCurrentPage}
            isLoading={isLoading}
            size={size}
            setSize={setSize}
            sizeAsRef={sizeAsRef}
            showLoadMore={showLoadMore}
            isEmpty={isEmpty}
            isValidating={isValidating}
            listType={JobListType.SEARCH_LIST_APISEARCH}
            locale={locale}
            renderBanner={(jobPosition) => {
              const showJobAlert = searchId && JOB_ALERT_BANNER_POSITIONS.includes(jobPosition as typeof JOB_ALERT_BANNER_POSITIONS[number]);

              // Tablet: show GH banner after first row; Desktop handled above.
              const shouldShowBannerByPosition = isTablet && jobPosition === HIRE_GUARANTEE_TABLET_START_POSITION;

              // Guaranteed Hiring banner: Only show if user doesn't have the package
              const showHireGuarantee = shouldShowBannerByPosition && (!isAuth || (!!headerInfoData && !headerInfoData.hireGuaranteeActive));

              if (!showJobAlert && !showHireGuarantee) return null;
              return (
                <div className="col-span-1 md:col-span-2 lg:col-span-3">
                  {showHireGuarantee && (
                    <GuaranteedHiringBanner />
                  )}
                  {showJobAlert && (
                    <JobAlertCard
                      createFn={createJobSearchAlert}
                      isSaved={isSavedJobSearchAlert}
                      isSaving={isCreatingJobSearchAlert}
                    />
                  )}
                </div>
              );
            }}
            title={title}
            paginationType="cursor"
          />

          {/* SEO Widget - Rendered only Server-Side - no need to update in client */}
          {seoData && (seoData.seoCities || seoData.seoDomains) && (
            <JobSeoWidget
              seoCities={seoData.seoCities}
              seoDomains={seoData.seoDomains}
              seoIsAbroad={seoData.seoIsAbroad}
            />
          )}

          {/* Loading Blurred Overlay */}
          {showLoadingOverlay && (
            <div className="absolute inset-0 cursor-wait z-2 backdrop-blur-xs" />
          )}
        </div>

      </div>
    </>
  );
};

// Page layout; Leverage the HeaderSearchContext.Provider in order to pass the
// query params to the search input component.
// ************************************************************************************
JobListPage.getLayout = function getLayout(page: ReactNode) {
  return (
    <FilterDrawerProvider>
      <LayoutDefault searchContext={headerSearchContextValue}>
        {page}
      </LayoutDefault>
    </FilterDrawerProvider>
  );
};

// GSSP API
// query and params are needed for the initial request used by the infinite scrolling
// ************************************************************************************
export const getServerSideProps: GetServerSideProps<JobListPageProps> = async (context) => {
  // Destructure context
  const {
    req,
    query,
  } = context;

  const locale = gsspGetLocale(query);

  // Server side translations
  const { t } = await getServerSideTranslation(locale, 'common');

  // Process the query to get the search params
  // query is like: { slug: [ 'full-time', 'beneficii', 'bonus-de-performanta', 'germana' ] }
  const {
    slug,
  } = query;

  const filterParams = getJobListParametersFromSlug(slug, t);

  // Process the query for the infinite scrolling. Use the returned object to pass parameters
  // to useSwrTalentSearchV2 hook and filters.
  const infiniteQueryParams = paginatedServerSideQuery({
    query,
    pageSlug: 'cursor',
    itemsPerPage: JOB_LIST_ITEMS_PER_PAGE,
    paginationType: 'cursor',
  });

  // Add location fallback from loadbalancer header if not already present
  const locationFromHeader = parseLocationHeader(req.headers['x-client-citylatlong'] as string);
  if (locationFromHeader && !filterParams._lat && !filterParams._lon && !query._lat && !query._lon) {
    filterParams._lat = locationFromHeader._lat;
    filterParams._lon = locationFromHeader._lon;
  }

  infiniteQueryParams.objFilterParams = filterParams as ObjectFilterParams;
  infiniteQueryParams.stringFilterParams = buildSearchParams(filterParams, '');

  infiniteQueryParams.fetchUrlParams += buildSearchParams(filterParams, '&');

  // Check if the user is authenticated
  // *****************************************
  const isAuthenticated = gsspCheckCookieAuth({ req }) as boolean;

  // Fetch data from server
  // *****************************************
  const [
    jobListCardsFromServer,
    breadcrumbsFromServer,
    seoDataFromServer,
  ] = await Promise.all([

    fetcher<ApiJobListCards>(`/v2/jobs${infiniteQueryParams.fetchUrlParams}`, locale, {
      ssrClientHeaders: req.headers,
    }),
    !isAuthenticated ? fetcher<ApiBreadcrumbs>(`/v1/jobs/breadcrumb${infiniteQueryParams.fetchUrlParams}`, locale, {
      ssrClientHeaders: req.headers,
    }) : Promise.resolve(),
    fetcher<ApiSchema<'SeoData'>>(`/v2/jobs/seo-data${infiniteQueryParams.fetchUrlParams}`, locale, {
      ssrClientHeaders: req.headers,
    }),
  ]);

  // Return props
  // *********************************
  return ({
    props: {
      ...(await serverSideTranslations(locale, ['common'])),
      locale,
      infiniteQueryParams,
      jobListCardsFromServer: isJobListCardsOk(jobListCardsFromServer) ? jobListCardsFromServer : null,
      breadcrumbsFromServer: isBreadcrumbsOk(breadcrumbsFromServer) ? breadcrumbsFromServer : null,
      seoDataFromServer: seoDataFromServer || null,
      isAuthenticated,
    },
  });
};

// Page export
// ************************************************************************************
export default JobListPage;
